// System status dashboard — super-admin only
import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Cpu, MemoryStick, HardDrive, Activity, Server, Database,
  RefreshCw, ShieldAlert, MousePointerClick, Eye, Building2, Globe,
  LayoutTemplate, Megaphone, ImageIcon, Clock, Package, TrendingUp,
} from "lucide-react";

const LEVEL_STYLES = {
  green: {
    bar: "bg-emerald-500",
    ring: "ring-emerald-500/20",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    softBg: "bg-emerald-50",
  },
  amber: {
    bar: "bg-amber-500",
    ring: "ring-amber-500/20",
    dot: "bg-amber-500",
    text: "text-amber-700",
    softBg: "bg-amber-50",
  },
  red: {
    bar: "bg-red-500",
    ring: "ring-red-500/20",
    dot: "bg-red-500",
    text: "text-red-700",
    softBg: "bg-red-50",
  },
};

function MetricCard({ icon: Icon, label, value, sub, level = "green", progress, testId }) {
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.green;
  return (
    <div className={`bg-white rounded-sm border border-slate-200 p-5 ring-1 ${s.ring}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
          <Icon className="w-4 h-4" />
          <span>{label}</span>
        </div>
        <div className={`w-2 h-2 rounded-full ${s.dot}`} title={level} />
      </div>
      <div className="text-2xl font-semibold text-slate-950 font-heading leading-none">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
      {typeof progress === "number" && (
        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${s.bar} transition-all`} style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 mt-8 first:mt-0">
      {children}
    </h2>
  );
}

export default function SystemStatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/system/metrics");
      setData(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Bilinmeyen hata";
      toast.error(`Sistem verileri alınamadı: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const host = data?.host;
  const app = data?.app;
  const traffic = data?.traffic;
  const entities = data?.entities;
  const plan = data?.plan;

  // Friendly "Nx upgrade" multiplier vs the previous plan (if any).
  const prev = plan?.history?.[0];
  const cur = plan?.current;
  let upgradeX = null;
  if (prev && cur?.cpu_vcpu && prev.cpu_vcpu) {
    upgradeX = Math.round(cur.cpu_vcpu / prev.cpu_vcpu);
  }

  return (
    <div className="space-y-6" data-testid="system-status-page">
      <PageHeader
        title="Sistem Durumu"
        subtitle="Sunucu kaynakları, uygulama sağlığı ve son 10 dakika trafik canary'si"
        actions={
          <div className="flex items-center gap-3">
            {data && (
              <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="system-generated-at">
                <Clock className="w-3.5 h-3.5" />
                {new Date(data.generated_at).toLocaleTimeString("tr-TR")}
              </div>
            )}
            <Button
              onClick={load}
              disabled={loading}
              variant="outline"
              className="rounded-sm h-10"
              data-testid="system-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Yenile
            </Button>
          </div>
        }
      />

      {!data ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-500" data-testid="system-loading">
          {loading ? "Yükleniyor…" : "Veri yok"}
        </div>
      ) : (
        <>
          {/* --- Paketiniz — büyük başlık kartı --- */}
          {cur && (
            <div
              className="bg-gradient-to-r from-emerald-50 via-white to-white border border-emerald-200 rounded-sm p-6 ring-1 ring-emerald-500/10"
              data-testid="plan-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-sm bg-emerald-100 text-emerald-700">
                    <Package className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
                      Aktif Paket
                    </div>
                    <div className="text-3xl font-bold text-slate-950 font-heading leading-tight mt-0.5" data-testid="plan-name">
                      {cur.name}
                    </div>
                    <div className="text-sm text-slate-600 mt-1.5">
                      <span className="font-semibold text-slate-800">{cur.cpu_vcpu} vCPU</span> ayrılmış ·{" "}
                      <span className="font-semibold text-slate-800">{cur.mem_mb >= 1024 ? (cur.mem_mb / 1024).toFixed(1) + " GB" : cur.mem_mb + " MB"}</span> RAM ayrılmış
                    </div>
                  </div>
                </div>
                {prev && upgradeX && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-sm bg-emerald-600 text-white text-sm font-medium shadow-sm" data-testid="plan-upgrade-badge">
                    <TrendingUp className="w-4 h-4" />
                    {upgradeX}x güçlendirildi
                  </div>
                )}
              </div>
              {prev && (
                <div className="mt-4 pt-4 border-t border-emerald-100 text-xs text-slate-500 flex flex-wrap items-center gap-2">
                  <span className="text-slate-400">Önceki paket:</span>
                  <span className="font-medium text-slate-700">{prev.name}</span>
                  <span className="text-slate-400">
                    ({prev.cpu_vcpu} vCPU / {prev.mem_mb >= 1024 ? (prev.mem_mb / 1024).toFixed(1) + " GB" : prev.mem_mb + " MB"})
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className="font-medium text-emerald-700">Bugün {cur.name}'e yükseltildi</span>
                </div>
              )}
            </div>
          )}

          {/* --- Katman A: Sunucu Kaynakları --- */}
          <SectionTitle>A · Sunucu Kaynakları</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Cpu}
              label="CPU"
              value={`${host.cpu_percent}%`}
              sub={`${host.cpu_count} çekirdek · yük ${host.load_avg_1m} / ${host.load_avg_5m} / ${host.load_avg_15m}`}
              level={host.cpu_level}
              progress={host.cpu_percent}
              testId="metric-cpu"
            />
            <MetricCard
              icon={MemoryStick}
              label="Bellek (RAM)"
              value={`${host.mem_percent}%`}
              sub={`${Math.round(host.mem_used_mb)} / ${Math.round(host.mem_total_mb)} MB`}
              level={host.mem_level}
              progress={host.mem_percent}
              testId="metric-ram"
            />
            <MetricCard
              icon={HardDrive}
              label="Disk"
              value={`${host.disk_percent}%`}
              sub={`${host.disk_used_gb} / ${host.disk_total_gb} GB`}
              level={host.disk_level}
              progress={host.disk_percent}
              testId="metric-disk"
            />
            <MetricCard
              icon={Server}
              label="Sunucu Çalışma Süresi"
              value={host.uptime_human}
              sub="Kesintisiz açık"
              level="green"
              testId="metric-uptime"
            />
          </div>

          {/* --- Katman B: Uygulama --- */}
          <SectionTitle>B · Uygulama (Backend Süreci)</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Activity}
              label="Süreç CPU"
              value={`${app.proc_cpu_percent}%`}
              sub={`Backend Python süreci`}
              level={app.proc_cpu_percent > 80 ? "red" : app.proc_cpu_percent > 50 ? "amber" : "green"}
              progress={app.proc_cpu_percent}
              testId="metric-proc-cpu"
            />
            <MetricCard
              icon={MemoryStick}
              label="Süreç Belleği"
              value={`${app.proc_mem_mb} MB`}
              sub="Backend RSS"
              level="green"
              testId="metric-proc-mem"
            />
            <MetricCard
              icon={Database}
              label="Redis Buffer"
              value={app.buffer_pending_events}
              sub={`${app.buffer_pending_keys} anahtar · DB'ye yazım bekleyen olay`}
              level={app.buffer_level}
              testId="metric-buffer"
            />
            <MetricCard
              icon={Clock}
              label="Backend Uptime"
              value={app.proc_uptime_human}
              sub="Son restart’tan beri"
              level="green"
              testId="metric-proc-uptime"
            />
          </div>

          {/* --- Katman C: Trafik & İş Sağlığı --- */}
          <SectionTitle>C · Trafik &amp; İş Sağlığı</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Eye}
              label="Son 10dk Gösterim"
              value={traffic.impressions_10m}
              sub={`Bot olmayan gerçek trafik`}
              level={traffic.traffic_level}
              testId="metric-imp-10m"
            />
            <MetricCard
              icon={MousePointerClick}
              label="Son 10dk Tıklama"
              value={traffic.clicks_10m}
              sub={`Aggregate olmuş (orphan hariç)`}
              level={traffic.traffic_level}
              testId="metric-clk-10m"
            />
            <MetricCard
              icon={ShieldAlert}
              label="Son 24s Yetkisiz Domain"
              value={traffic.unauthorized_domain_24h}
              sub="Whitelist reddi"
              level={traffic.unauthorized_level}
              testId="metric-unauth-24h"
            />
            <MetricCard
              icon={ShieldAlert}
              label="Son 24s Orphan Click"
              value={traffic.orphan_click_24h}
              sub="Impression’sız tıklama (reddedildi)"
              level={traffic.orphan_level}
              testId="metric-orphan-24h"
            />
          </div>

          {/* --- Aktif Varlıklar --- */}
          <SectionTitle>Aktif Varlıklar</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <MetricCard icon={Building2} label="Belediye" value={entities.active_municipalities} level="green" testId="entity-mun" />
            <MetricCard icon={Globe} label="Yayıncı Site" value={entities.active_sites} level="green" testId="entity-sites" />
            <MetricCard icon={LayoutTemplate} label="Reklam Alanı" value={entities.active_placements} level="green" testId="entity-placements" />
            <MetricCard icon={Megaphone} label="Kampanya" value={entities.active_campaigns} level="green" testId="entity-campaigns" />
            <MetricCard icon={ImageIcon} label="Kreatif" value={entities.active_creatives} level="green" testId="entity-creatives" />
          </div>

          <div className="text-xs text-slate-400 pt-4 border-t border-slate-100">
            Türk ADS CDN · Sürüm v{data.version} · Anlık ölçüm, otomatik yenileme yok — güncel değerler için <span className="font-semibold">Yenile</span> butonuna basın.
          </div>
        </>
      )}
    </div>
  );
}
